import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import * as types from '../../../../constants/actionTypes';
import Routes from '../../../../constants/routes';
import LoadingIndicator from '../../../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../../../Common/Notification';
import Icon from '../../../Common/Icon';
import { TASK_LIST_VIEWS as VIEWS, TASK_LIST_MENU_ITEMS } from '../../../../constants/constants';
import { CUSTOMER_ROLES } from '../../../../constants/roles';
import '../TaskListPage.scss';
import LocationSelectorDrawer from '../../../Common/LocationSelector/LocationSelectorDrawer';
import MultiSelectBox from '../../../Common/MultiSelectBox/MultiSelectBox';
import { NavigateToRouteWithDashboardParams } from '../../../../helpers/routing';
import TaskListOption from '../../TaskListOption';

import './TaskListHeader.scss';

const MANAGE_CUSTOM_VIEWS_TEXT = 'Manage Custom Views';
const MANAGE_CUSTOM_VIEWS_ID = -91;
const MAX_LOCATION_SELECTOR_DISPLAY_LENGTH = 50;
const SAVE_CURRENT_VIEW_TEXT = 'Save Current View';
const SAVE_CURRENT_VIEW_ID = -92;

const viewOptions = [
	{ id: VIEWS.List.id, name: VIEWS.List.label, icon: VIEWS.List.icon, value: VIEWS.List.value },
	{ id: VIEWS.Calendar.id, name: VIEWS.Calendar.label, icon: VIEWS.Calendar.icon, value: VIEWS.Calendar.value },
	{ id: VIEWS.Gantt.id, name: VIEWS.Gantt.label, icon: VIEWS.Gantt.icon, value: VIEWS.Gantt.value },
	{
		id: VIEWS.ResourceAllocation.id,
		name: VIEWS.ResourceAllocation.label,
		icon: VIEWS.ResourceAllocation.icon,
		value: VIEWS.ResourceAllocation.value,
	},
];

export class TaskListHeader extends React.Component {
	constructor(props, context) {
		super(props, context);

		this.state = {
			isLoading: false,
			showLocationSelectorDrawer: false,
			taskMenuItems: [],
			selectedIndex: 0,
		};
	}

	componentDidMount = async () => {
		try {
			await this.initTaskListHeader();
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	componentDidUpdate = () => {
		this.buildTaskListItems();
	};

	buildLocationsDisplayValues = (locations) => {
		if (locations && locations.length > 0) {
			const dipslayValues = locations.map((x) => {
				const ellipsis = '...';
				const maxDisplayValueLength = MAX_LOCATION_SELECTOR_DISPLAY_LENGTH - ellipsis.length;
				let displayValue = `${x.name} (${x.abbreviation})`;
				if (displayValue.length > maxDisplayValueLength) {
					const diff = displayValue.length - maxDisplayValueLength;
					const truncatedLength = x.name.length - diff;
					const truncatedName = x.name.substring(0, truncatedLength).trimEnd();
					displayValue = `${truncatedName}${ellipsis} (${x.abbreviation})`;
				}
				// Keep extra location info that we need elsewhere on the task list
				return { id: x.locationKey, name: displayValue, openingDate: x.openingDate };
			});
			return dipslayValues;
		}
		return [];
	};

	buildTaskListItems = () => {
		const redux = this.props.taskListState;

		let items = redux.tasksPageTasksMenuItems.map((mi, idx) => {
			return { id: idx, item: mi };
		});

		if (!_.isEqual(items, this.state.taskMenuItems)) {
			this.setState({ taskMenuItems: items });
		}
	};

	initTaskListHeader = async () => {
		try {
			this.buildTaskListItems();

			const redux = this.props.taskListState;
			let locationKeys = redux && redux.searchParams && redux.searchParams.locationKeys ? redux.searchParams.locationKeys : [];
			
			// Include URL location parameters to ensure they are loaded even if not yet in Redux state
			const hasUrlLocationParams = this.props.urlParams && this.props.urlParams.locationKeys && this.props.urlParams.locationKeys.length > 0;
			if (hasUrlLocationParams) {
				// Merge URL location keys with existing ones, removing duplicates
				locationKeys = [...new Set([...locationKeys, ...this.props.urlParams.locationKeys])];
			}
			
			let response;
			// Always use the merged location keys (includes both Redux and URL parameters)
			const request = {
				subscriptionId: this.props.subscriptionId,
				locationKeys: locationKeys,
			};
			response = await this.props.locationTaskActions.loadInitialLocationSelectorFilters(request);
			if (response.type === types.LOAD_INITIAL_LOCATION_SELECTOR_FILTERS_SUCCESS || response.type === types.LOAD_LOCATION_SELECTOR_FILTERS_SUCCESS) {
				const locations = this.buildLocationsDisplayValues(response.payload.locations);
				await this.props.locationTaskActions.setLocationSelectorFilters(locations, response.payload.defaultLocationKey);

				// Apply default location if no locations are selected (URL or Redux)
				const reduxHasLocations = redux.searchParams && redux.searchParams.locationKeys && redux.searchParams.locationKeys.length > 0;

				if (!reduxHasLocations && !hasUrlLocationParams && redux.defaultLocationKey && redux.defaultLocationKey.length > 0) {
					// No locations anywhere - fall back to default
					this.props.onLocationChange([redux.defaultLocationKey], true);
				}
			} else if (response.type === types.LOAD_INITIAL_LOCATION_SELECTOR_FILTERS_FAILURE || response.type === types.LOAD_LOCATION_SELECTOR_FILTERS_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	loadLocationSelectorFilters = async () => {
		try {
			this.setState({ isLoading: true });
			const response = await this.props.locationTaskActions.loadLocationSelectorFilters(this.props.subscriptionId);
			if (response.type === types.LOAD_LOCATION_SELECTOR_FILTERS_SUCCESS) {
				const locations = this.buildLocationsDisplayValues(response.payload.locations);
				await this.props.locationTaskActions.setLocationSelectorFilters(locations, response.payload.defaultLocationKey);
				this.setState({ isLoading: false });
			} else if (response.type === types.LOAD_LOCATION_SELECTOR_FILTERS_FAILURE) {
				Notification('error', 'Error', response.payload.message);
				this.setState({ isLoading: false });
			}
		} catch (error) {
			Notification('error', 'Error', error);
			this.setState({ isLoading: false });
		}
	};

	onViewChange = (value) => {
		this.props.onViewChange(value);
	};

	onAddNew = (e) => {
		if (e.shiftKey) {
			this.props.onAddNewTask();
		} else {
			this.props.onAddNewTaskLegacy();
		}
	};

	onBulkUpdate = () => {
		this.props.changeBulkUpdateMode();
	};

	onClearFilters = () => {
		this.props.onClearFilters();
	};

	onColumnsClick = () => {
		this.props.onColumnsClick();
	};

	getSelectedLocationsDisplayNames = (redux) => {
		if (
			redux.searchParams &&
			redux.locations &&
			redux.locations.length > 0 &&
			redux.searchParams.locationKeys &&
			redux.searchParams.locationKeys.length > 0
		) {
			let retval = '';
			const selectedLocations = redux.locations.filter((m) => redux.searchParams.locationKeys.indexOf(m.id) >= 0);

			if (selectedLocations.length > 3) {
				return selectedLocations.length + ' Locations Displayed';
			} else {
				for (let i = 0; i < selectedLocations.length; i++) {
					const location = selectedLocations[i];
					retval += location.name + ' ';
					if (i !== selectedLocations.length - 1) {
						retval += ', ';
					}
				}
			}

			return retval;
		} else {
			return '';
		}
	};

	onOpenLocationSelector = async () => {
		this.setState({ showLocationSelectorDrawer: true });

		const redux = this.props.taskListState;
		const locationKeys = redux && redux.searchParams && redux.searchParams.locationKeys ? redux.searchParams.locationKeys : [];
		const locations = redux && redux.locations ? redux.locations : [];
		if (locationKeys.length >= locations.length) {
			/**
			 *  Only load all locations if the locations list only contains the
			 * currently-selected values.  If we've already loaded the locations
			 * list with ALL accessible locations (e.g. more than values than
			 * currently selected) then we don't need to call this again to
			 * reload all locations again, just because they've chosen to open
			 * the location selector drawer again.  This saves an operation and
			 * hopefully helps performance on subscriptions with many locations.
			 */
			await this.loadLocationSelectorFilters();
		}
	};

	onLocationSelectionComplete = (selectedLocations) => {
		const locationKeys = selectedLocations.map((l) => l.id);

		this.setState({ showLocationSelectorDrawer: false }, () => {
			this.props.locationTaskActions.setLocationSelectorFilters(selectedLocations, selectedLocations[0]?.id);
			this.props.onLocationChange(locationKeys, false);
		});
	};

	onLocationSelectionCancel = () => {
		this.setState({ showLocationSelectorDrawer: false });
	};

	getTasksMenuOptions = () => {
		const { isSaveAndManageTaskFiltersActive } = this.props;
		const { taskMenuItems } = this.state;

		const mapTaskMenuItems = () =>
			taskMenuItems.map((mi) => ({
				icon: 'list-check',
				id: mi.id,
				name: mi.item.filterName,
			}));

		const addCustomViewOptions = (isActive) => [
			{
				icon: 'list-check',
				itemIcon: isActive ? 'pen' : 'lock',
				id: MANAGE_CUSTOM_VIEWS_ID,
				name: MANAGE_CUSTOM_VIEWS_TEXT,
				isSeparator: true,
			},
			{
				icon: 'list-check',
				itemIcon: isActive ? 'floppy-disk' : 'lock',
				id: SAVE_CURRENT_VIEW_ID,
				name: SAVE_CURRENT_VIEW_TEXT,
				isSeparator: false,
			},
		];

		return [...mapTaskMenuItems(), ...addCustomViewOptions(isSaveAndManageTaskFiltersActive)];
	};

	onTasksMenuChange = async (menuItemValue) => {
		const { onSaveViewClick, onCustomViewClick, locationTaskActions, taskListState, isSaveAndManageTaskFiltersActive } = this.props;
		const { taskMenuItems } = this.state;

		const filter = taskMenuItems.find((x) => x.id === menuItemValue);

		if (menuItemValue === SAVE_CURRENT_VIEW_ID) {
			if (!isSaveAndManageTaskFiltersActive) return;

			onSaveViewClick(); // Prompt user with dialog to save current filters
		} else if (menuItemValue === MANAGE_CUSTOM_VIEWS_ID) {
			if (!isSaveAndManageTaskFiltersActive) return;

			this.props.history.push(Routes.ManageTaskViews); // Take user to manage views page
		} else if (filter) {
			await locationTaskActions.setTasksMenuSelected(menuItemValue);

			if (filter.item.isCustom) {
				await onCustomViewClick(filter.item.filterName);
			} else {
				const menuItem = taskListState.tasksMenuItems.find((x) => x.displayName.toLowerCase() === filter.item.filterName.toLowerCase());
				
				// Special handling for "Tasks" menu item - navigate internally and set default parameters
				if (menuItem.displayName.toLowerCase() === TASK_LIST_MENU_ITEMS.Tasks.value.toLowerCase()) {
					// Navigate to Tasks route without URL parameters to show default state
					this.props.history.push(Routes.Tasks);
				} else {
					NavigateToRouteWithDashboardParams(Routes.Tasks, menuItem.urlParams, false);
				}
			}
		}
	};

	handleTabClick = (id) => {
		this.setState({ selectedIndex: id });
		var filteredView = Object.values(viewOptions).find((view) => view.id === id);
		this.props.onViewChange(filteredView?.value);
	};

	render() {
		const redux = this.props.taskListState;

		// Disable the Add Task button, unless there is only a single location selected
		// Also hide the option to bulk update by job title unless there is only a single location selected
		const isMultipleLocationsDisabled = redux.searchParams.locationKeys.length > 1;

		const toCamelCase = (str) =>
			typeof str === 'string' && str.trim()
				? str
						.replace(/\s(.)/g, (s) => s[1].toUpperCase())
						.replace(/\s/g, '')
						.replace(/^(.)/, (s) => s.toLowerCase())
				: '';

		const isNumber = typeof redux.tasksMenuSelectedValue === 'number';

		const selectedItem =
			this.state.taskMenuItems.length &&
			this.state.taskMenuItems.find((mi) =>
				isNumber
					? mi.id === redux.tasksMenuSelectedValue
					: toCamelCase(redux.tasksMenuSelectedValue.displayName) === toCamelCase(mi.item?.filterName || '')
			);

		const defaultItem =
			this.state.taskMenuItems.length &&
			this.state.taskMenuItems.find((mi) => mi.item && mi.item.filterName.toLowerCase() === TASK_LIST_MENU_ITEMS.Tasks.value.toLowerCase());

		const tasksMenuSelectedValue = selectedItem
			? redux.tasksMenuSelectedValue !== undefined
				? selectedItem.id
				: ''
			: defaultItem === undefined
			? ''
			: String(defaultItem.id);

		return (
			<LoadingIndicator isLoading={this.state.isLoading}>
				<div className='align-items-center mb-3 task-list-header'>
					<div className='location-selector col-md-5'>
						<div className='alert alert-light d-flex justify-content-between align-items-center' onClick={this.onOpenLocationSelector}>
							{this.getSelectedLocationsDisplayNames(redux)}
							<Icon name='caret-down' />
						</div>
					</div>
					{!this.props.isMobile ? (
						<div className='col-md-7 d-flex justify-content-end align-items-center'>
							{this.props.bulkUpdateMode && (
								<Fragment>
									{!isMultipleLocationsDisabled && (
										<button className='btn btn-outline-secondary me-2' onClick={this.props.bulkUpdateByJobTitle}>
											<Icon name='user-tag' /> Assign By Job Title
										</button>
									)}
									<button className='btn btn-outline-secondary me-2' onClick={this.props.bulkUpdateResponsibleUser}>
										<Icon name='user' /> Change Responsible
									</button>
									<button className='btn btn-outline-secondary me-2' onClick={this.props.bulkUpdateStatus}>
										<Icon name='heartbeat' /> Change Status
									</button>
									{[CUSTOMER_ROLES.Admin.name, CUSTOMER_ROLES.Owner.name].includes(this.props.userRole) && (
										<button className='btn btn-outline-secondary me-2' onClick={this.props.bulkUpdateInactive}>
											<Icon name='minus-circle' /> Inactive
										</button>
									)}
									<button className='btn btn-outline-danger' onClick={this.onBulkUpdate}>
										<Icon name='dot-circle' /> End Bulk Update
									</button>
								</Fragment>
							)}
						</div>
					) : null}
				</div>

				<div className='task-list'>
					{/* Responsive container for tabs and controls */}
					<div className='row g-0'>
						{/* Tabs section - takes full width on small screens, auto width on larger screens */}
						<div className='col-12 col-lg-auto flex-lg-grow-1'>
							<ul className='nav nav-tabs w-100 visual-tabs'>
								{viewOptions.map((tab) => (
									<li key={tab.id} className={`nav-item ${this.state.selectedIndex === tab.id ? 'active' : ''}`}>
										<a
											className={`nav-link ${this.state.selectedIndex === tab.id ? 'active' : ''}`}
											onClick={() => this.handleTabClick(tab.id)}>
											<i className={`fa-regular ${tab.icon} me-2`}></i>
											<span>{tab.name}</span>
										</a>
									</li>
								))}
							</ul>
						</div>
						
						{/* Controls section - stacks below tabs on small screens, inline on larger screens */}
						<div className='col-12 col-lg-auto'>
							<div className='d-flex gap-2 align-items-center justify-content-end justify-content-lg-end'>
								{this.props.selectedView === VIEWS.List.value && (
									<MultiSelectBox
										className='custom-multiselect'
										name='tasksMenu'
										idField='id'
										valueField='name'
										options={this.getTasksMenuOptions()}
										onChange={this.onTasksMenuChange}
										selectedValues={tasksMenuSelectedValue.toString()}
										isMulti={false}
										isSearchable={false}
										optionComponent={TaskListOption}
										shouldShowIcons={false}
										forceDesktopLayout={true}
									/>
								)}
								<button className='btn btn-outline-primary d-flex align-items-center gap-2' onClick={this.props.onFiltersClick}>
									<i className='fa-regular fa-bars-filter'></i>
									<span className='d-none d-sm-inline'>Filters</span>
								</button>
								<button
									className='btn btn-primary d-flex align-items-center gap-2'
									disabled={isMultipleLocationsDisabled}
									onClick={this.onAddNew}>
									<i className='fa-regular fa-circle-plus'></i>
									<span className='d-none d-sm-inline'>Add Task</span>
								</button>
							</div>
						</div>
					</div>
				</div>
				<LocationSelectorDrawer
					onComplete={this.onLocationSelectionComplete}
					onCancel={this.onLocationSelectionCancel}
					isSingleSelect={false}
					isOpen={this.state.showLocationSelectorDrawer}
				/>
			</LoadingIndicator>
		);
	}
}

TaskListHeader.propTypes = {
	isMobile: PropTypes.bool.isRequired,
	subscriptionId: PropTypes.string.isRequired,
	locationTaskActions: PropTypes.object.isRequired,
	taskListState: PropTypes.object.isRequired,
	urlParams: PropTypes.object.isRequired,
	selectedView: PropTypes.string.isRequired,
	onViewChange: PropTypes.func.isRequired,
	onLocationChange: PropTypes.func.isRequired,
	onColumnsClick: PropTypes.func.isRequired,
	onFiltersClick: PropTypes.func.isRequired,
	bulkUpdateMode: PropTypes.bool.isRequired,
	changeBulkUpdateMode: PropTypes.func.isRequired,
	bulkUpdateResponsibleUser: PropTypes.func.isRequired,
	bulkUpdateStatus: PropTypes.func.isRequired,
	bulkUpdateInactive: PropTypes.func.isRequired,
	bulkUpdateByJobTitle: PropTypes.func.isRequired,
	hasNoFilters: PropTypes.bool.isRequired,
	onClearFilters: PropTypes.func.isRequired,
	userRole: PropTypes.string.isRequired,
	onAddNewTask: PropTypes.func.isRequired,
	onAddNewTaskLegacy: PropTypes.func.isRequired,
	onSaveViewClick: PropTypes.func.isRequired,
	onCustomViewClick: PropTypes.func.isRequired,
	onGanttZoomIn: PropTypes.func,
	onGanttZoomOut: PropTypes.func,
	onGanttExportToPDF: PropTypes.func,
	onGanttExportToPNG: PropTypes.func,
	history: PropTypes.object.isRequired,
	isSaveAndManageTaskFiltersActive: PropTypes.bool.isRequired,
};

export default TaskListHeader;
